/* global google */
import _ from '@rover/globals/underscore';
import $ from '@rover/globals/jquery';
import Rover from '@rover/globals/Rover';
import { redirect, addEventParamsToUrl } from '@rover/utilities/url';
import inject from '@rover/utilities/injector';

import StaticStickitView from '../static_stickit_view';

export default StaticStickitView.extend({
  // A view for the search location text box.  It takes a Query
  // as its model, and the text box is bound to its 'location'
  // attribute.  We only trigger a change on the query object on
  // the 'change' event as opposed to all of stickits default events:
  // ('keyup', etc.).
  //
  // When a location is selected from the autocomplete, or the 'search'
  // button is clicked, we geocode the location and redirect to a new
  // search page with those inputs.  This is done so that the new page
  // can display e.g. SEO URLs and copy.
  bindings: {
    '.js-service-name': {
      observe: 'service_type',
      onGet(value) {
        const serviceType = Rover.serviceTypesJson.find(st => st.slug === value);
        return (serviceType && serviceType.name) || '';
      },
    },
    '.js-search-location': {
      observe: 'location',
      events: ['change'],
    },
  },

  events: {
    'click .js-search-search-button': 'startNewSearch',
  },

  initialize(options) {
    this.$input = this.$('.js-search-location');
    this.triggerGeoCodeOnChange = options.triggerGeoCodeOnChange || false;
    this.geoCodeWithoutPageRefresh = options.geoCodeWithoutPageRefresh || false;
    this.searchResults = options.searchResults;
    this.isSearchEnabled = options.isSearchEnabled;

    // Perform a search on enter
    this.$input.on('keyup', _.bind(this.onEnterPressed, this));

    // Clear the input on click
    this.$input.on('click', _.bind(this.onClick, this));

    if (this.triggerGeoCodeOnChange) {
      this.$input.on('change', _.bind(this.onLocationInputChanged, this));
    }

    if (Object.prototype.hasOwnProperty.call(window, 'google')) {
      this.service = new google.maps.places.AutocompleteService();
      this.geocoder = new google.maps.Geocoder();

      // Initialize typeahead on the location bar
      this.$input.typeahead({
        item: '<li><a href="#"></a></li>',
        source: _.bind(this.getPlacePredictions, this),
        select: this.typeaheadSelect,
        autoSelect: false,
        menu: '<ul class="typeahead dropdown-menu location-dropdown"></ul>',
        matcher(item) {
          const queryWithoutSpaces = this.query.replace(/ /g, '');
          const itemWithoutSpaces = item.replace(/ /g, '');
          return itemWithoutSpaces.toLowerCase().includes(queryWithoutSpaces.toLowerCase());
        },
      });

      // Watch for selecting an autocomplete choice
      this.$input.on('typeahead:selected', _.bind(this.startNewSearch, this));
    }

    _.bindAll(this, 'redirectToSearchPageFor');

    StaticStickitView.prototype.initialize.call(this, options);
  },

  typeaheadSelect() {
    // A custom select function for typeahead which triggers
    // a custom event
    const val = this.$menu.find('.active').data('value');
    if (this.autoSelect || val) {
      this.$element.val(this.updater(val)).change();
      this.$element.trigger('typeahead:selected');
    }
    return this.hide();
  },

  getPlacePredictions(query, callback) {
    const options = {
      input: query || {},
      types: ['geocode'],
    };

    this.service.getPlacePredictions(options, (predictions, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK) {
        const descriptions = predictions.map(r => r.description);
        callback(descriptions);
      }
    });
  },

  onEnterPressed(event) {
    if (event.keyCode === 13) {
      this.startNewSearch();
    }
  },

  onClick() {
    this.setLocation('');
  },
  onLocationInputChanged() {
    this.geocodeLocation();
  },
  startNewSearch() {
    this.dataLayerEvent = {
      event: 'search-nav-bar',
      zipcode: this.getLocation(),
    };
    this.geocodeLocation();
  },

  geocodeLocation() {
    this.$input.data('geocoded', '');
    if (!this.geocoder) {
      this.redirectToSearchPageFor(null, null);
      return;
    }

    const address = this.getLocation();
    const region = inject('Rover.context.ccTLD', 'US');

    this.geocoder.geocode({ address, region }, (results, status) => {
      if (results && status === google.maps.GeocoderStatus.OK) {
        const [prevLat, prevLng] = Rover.searchResults.safeGet('derived_data.center', []);
        const lat = results[0].geometry.location.lat();
        const lng = results[0].geometry.location.lng();

        if (lat !== prevLat || lng !== prevLng) {
          if (this.geoCodeWithoutPageRefresh) {
            Rover.query.set(this.getSearchParamsFor(lat, lng));
          } else {
            this.redirectToSearchPageFor(lat, lng);
          }
        }
      }
    });
  },

  getLocation() {
    return this.$input.val();
  },

  setLocation(name) {
    this.$input.val(name);
  },

  getSearchParamsFor(lat, lng) {
    return {
      location: this.getLocation(),
      centerlat: lat,
      centerlng: lng,
      minlat: null,
      maxlat: null,
      minlng: null,
      maxlng: null,
      location_type: null,
      zoomlevel: null, // leaving in zoomlevel appears to cache search results
    };
  },

  redirectToSearchPageFor(lat, lng) {
    this.searchParams = this.getSearchParamsFor(lat, lng);
    this.trigger('geocode:complete', this.searchParams);

    if (this.$input) {
      this.$input.data('geocoded', 'geocoded');
      this.$input.trigger('geocoded');
    }

    const params = $.param($.extend(this.model.toJSON(), this.searchParams), true);
    const queryString = `?${params}`;

    if (this.isSearchEnabled) {
      const url = addEventParamsToUrl(Rover.urls.search + queryString, [this.dataLayerEvent]);
      redirect(url);
    }
  },
});



// WEBPACK FOOTER //
// ./js/backbone/views/search/search_location_view.js